import React, { useEffect, useState } from "react";
import { useNavigate, Link, Navigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Eye, EyeOff, ArrowLeft, MailCheck } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { CodeInput } from "../../components/CodeInput";

const AUTH_IMAGE = "/bg1.jpeg";

export default function LoginPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();
    const { loginInitiate, loginVerify, isAuthenticated, loading } = useAuth();
    const [step, setStep] = useState("creds"); // "creds" | "code"
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [code, setCode] = useState("");
    const [show, setShow] = useState(false);
    const [busy, setBusy] = useState(false);
    const [resendCount, setResendCount] = useState(0);

    useEffect(() => {
        if (params.get("session") === "expired") {
            toast.info(t("auth.session_expired"));
            // Clear the query param so the toast doesn't fire again on remount
            const next = new URLSearchParams(params);
            next.delete("session");
            setParams(next, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Redirect if already authenticated
    if (!loading && isAuthenticated) return <Navigate to="/bookings" replace />;

    const submitCreds = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await loginInitiate(email, password);
            setStep("code");
            toast.success(t("auth.code_sent"));
        } catch (err) {
            const status = err?.response?.status;
            if (status === 401) toast.error(t("auth.invalid_credentials"));
            else if (status === 404) toast.error(t("auth.no_account_with_email"));
            else if (status === 429) toast.error(t("auth.too_many_requests"));
            else if (status === 422) toast.error(t("auth.invalid_email_format"));
            else toast.error(t("common.error"));
        } finally {
            setBusy(false);
        }
    };

    const submitCode = async (e) => {
        e.preventDefault();
        if (code.length !== 6) return;
        setBusy(true);
        try {
            await loginVerify(email, code);
            toast.success(t("auth.welcome_back"));
            navigate("/bookings", { replace: true });
        } catch (err) {
            const detail = err?.response?.data?.detail || "";
            if (detail.toLowerCase().includes("invalid code")) toast.error(t("auth.invalid_code"));
            else if (detail.toLowerCase().includes("expired")) toast.error(t("auth.code_expired"));
            else if (detail.toLowerCase().includes("attempts")) toast.error(t("auth.too_many_attempts"));
            else toast.error(t("common.error"));
        } finally {
            setBusy(false);
        }
    };

    const resend = async () => {
        if (resendCount >= 2) {
            toast.error(t("auth.too_many_requests"));
            return;
        }
        try {
            await loginInitiate(email, password);
            setResendCount(resendCount + 1);
            toast.success(t("auth.code_resent"));
        } catch (err) {
            toast.error(t("common.error"));
        }
    };

    return (
        <div data-testid="login-page" className="min-h-[calc(100vh-4rem)] grid md:grid-cols-2">
            <div className="flex items-center justify-center p-6 md:p-16">
                <div className="w-full max-w-md space-y-8">
                    {step === "creds" ? (
                        <>
                            <div className="space-y-2">
                                <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tight">
                                    {t("auth.login_title")}
                                </h1>
                                <p className="text-muted-foreground font-medium">{t("auth.login_subtitle")}</p>
                            </div>
                            <form onSubmit={submitCreds} className="space-y-4" data-testid="login-form">
                                <div>
                                    <label className="text-sm font-semibold block mb-1.5">{t("auth.email")}</label>
                                    <input
                                        data-testid="login-email"
                                        type="email"
                                        required
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="input-modern"
                                        placeholder="you@email.com"
                                    />
                                </div>
                                <div>
                                    <label className="text-sm font-semibold block mb-1.5">{t("auth.password")}</label>
                                    <div className="relative">
                                        <input
                                            data-testid="login-password"
                                            type={show ? "text" : "password"}
                                            required
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            className="input-modern pr-12"
                                            placeholder="••••••••"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShow(!show)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                                        >
                                            {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                        </button>
                                    </div>
                                </div>
                                <div className="text-right -mt-1">
                                    <Link
                                        to="/forgot-password"
                                        data-testid="forgot-password-link"
                                        className="text-sm font-semibold text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
                                    >
                                        {t("auth.forgot_password")}
                                    </Link>
                                </div>
                                <button
                                    type="submit"
                                    data-testid="login-submit"
                                    disabled={busy}
                                    className="btn-primary w-full disabled:opacity-50"
                                >
                                    {busy ? t("common.loading") : t("auth.login")}
                                </button>
                            </form>
                            <div className="text-center text-sm">
                                <span className="text-muted-foreground">{t("auth.no_account")} </span>
                                <Link to="/register" data-testid="login-to-register" className="font-bold underline">
                                    {t("auth.sign_up_now")}
                                </Link>
                            </div>
                        </>
                    ) : (
                        <>
                            <button
                                type="button"
                                onClick={() => { setStep("creds"); setCode(""); }}
                                className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground"
                                data-testid="login-code-back"
                            >
                                <ArrowLeft className="h-4 w-4" />
                                {t("common.back")}
                            </button>
                            <div className="space-y-2">
                                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-muted text-xs font-semibold">
                                    <MailCheck className="h-3.5 w-3.5" />
                                    {t("auth.code_sent_short")}
                                </div>
                                <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tight">
                                    {t("auth.enter_code_title")}
                                </h1>
                                <p className="text-muted-foreground font-medium">
                                    {t("auth.enter_code_subtitle", { email })}
                                </p>
                            </div>
                            <form onSubmit={submitCode} className="space-y-6">
                                <CodeInput value={code} onChange={setCode} testIdPrefix="login-code" />
                                <button
                                    type="submit"
                                    data-testid="login-code-submit"
                                    disabled={busy || code.length !== 6}
                                    className="btn-primary w-full disabled:opacity-50"
                                >
                                    {busy ? t("common.loading") : t("auth.verify")}
                                </button>
                                <div className="text-center text-sm">
                                    <button
                                        type="button"
                                        data-testid="login-code-resend"
                                        onClick={resend}
                                        className="font-semibold underline-offset-4 hover:underline"
                                    >
                                        {t("auth.resend_code")}
                                    </button>
                                </div>
                            </form>
                        </>
                    )}
                </div>
            </div>
            <div className="hidden md:block relative">
                <img src={AUTH_IMAGE} alt="Concert vibes" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-br from-black/40 to-black/60" />
                <div className="absolute bottom-10 left-10 right-10 text-white">
                    <img src="/logo-youvibeticket-wht.png" alt="youvibeticket" className="h-12 w-auto mb-2" />
                    <p className="text-white/80">{t("brand.tagline")}</p>
                </div>
            </div>
        </div>
    );
}
